import React, { useEffect, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InviteUserDialogProps {
  onInviteSent?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteUserDialog = ({ onInviteSent }: InviteUserDialogProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'designer' | 'recruiter' | 'superadmin'>('designer');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const { toast } = useToast();

  const trimmedEmail = email.trim().toLowerCase();
  const isValidFormat = EMAIL_REGEX.test(trimmedEmail);

  // Debounced preemptive check against profiles table
  useEffect(() => {
    if (!open) return;
    if (!trimmedEmail) {
      setEmailError(null);
      setCheckingEmail(false);
      return;
    }
    if (!isValidFormat) {
      setEmailError(null);
      setCheckingEmail(false);
      return;
    }

    let cancelled = false;
    setCheckingEmail(true);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', trimmedEmail)
        .maybeSingle();

      if (cancelled) return;
      setCheckingEmail(false);
      if (error) {
        // Non-blocking — let server-side validation catch it
        return;
      }
      if (data) {
        setEmailError('A user with this email already exists');
      } else {
        setEmailError(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmedEmail, isValidFormat, open]);

  const handleSendInvitation = async () => {
    if (!trimmedEmail || !isValidFormat) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (emailError) return;

    try {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast({
          title: 'Authentication error',
          description: 'You must be logged in to send invitations',
          variant: 'destructive',
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('send-user-invitation', {
        body: {
          email: trimmedEmail,
          role,
        },
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to send invitation');
      }

      toast({
        title: 'Invitation sent!',
        description: `An invitation has been sent to ${trimmedEmail}`,
      });

      setEmail('');
      setRole('designer');
      setEmailError(null);
      setOpen(false);

      if (onInviteSent) {
        onInviteSent();
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);

      const raw = error?.message || '';
      let inlineError: string | null = null;
      let toastMessage = 'Failed to send invitation';

      if (raw.includes('user_exists')) {
        inlineError = 'A user with this email already exists';
      } else if (raw.includes('invitation_exists')) {
        inlineError = 'An active invitation already exists for this email';
      } else if (raw.includes('forbidden')) {
        toastMessage = 'You do not have permission to send invitations';
      } else if (raw) {
        toastMessage = raw;
      }

      if (inlineError) {
        setEmailError(inlineError);
      } else {
        toast({
          title: 'Error',
          description: toastMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inviteDisabled =
    loading || !trimmedEmail || !isValidFormat || !!emailError || checkingEmail;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" className="bg-black hover:bg-gray-800 text-white">
          Add user
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium leading-none mb-3">Add user</h4>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="tham@wcd.example"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(null);
              }}
              disabled={loading}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'email-error' : undefined}
              className={cn('h-9', emailError && 'border-destructive focus-visible:ring-destructive')}
            />
            {emailError && (
              <p id="email-error" className="text-xs text-destructive">
                {emailError}
              </p>
            )}
            {!emailError && checkingEmail && (
              <p className="text-xs text-muted-foreground">Checking…</p>
            )}
          </div>

          <div className="space-y-3">
            <RadioGroup value={role} onValueChange={(value) => setRole(value as any)}>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value="designer" id="designer" disabled={loading} className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="designer" className="text-sm font-medium cursor-pointer">
                    Designer
                  </Label>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Can create and manage their own dashboard and apply to the vetting program
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="recruiter" id="recruiter" disabled={loading} className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="recruiter" className="text-sm font-medium cursor-pointer">
                    Recruiter
                  </Label>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Can post job descriptions, manage shortlists, archive candidates
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <RadioGroupItem value="superadmin" id="superadmin" disabled={loading} className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="superadmin" className="text-sm font-medium cursor-pointer">
                    Super Admin
                  </Label>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Can manage user roles, users, permissions, and system settings
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <Button
            onClick={handleSendInvitation}
            disabled={inviteDisabled}
            className="w-full bg-black hover:bg-gray-800 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Invite'
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InviteUserDialog;
